import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Users, AlertTriangle, Link2, Server } from 'lucide-react';
import firmService from '../../services/firmService';
import observabilityService from '../../services/observabilityService';
import ObservabilityFirmPicker from '../../components/admin/ObservabilityFirmPicker';
import OverviewTab from '../../components/admin/OverviewTab';
import UsersTab from '../../components/admin/UsersTab';
import ErrorsTab from '../../components/admin/ErrorsTab';
import IntegrationsTab from '../../components/admin/IntegrationsTab';
import SystemTab from '../../components/admin/SystemTab';

const tabs = [
  { id: 'overview', name: 'Overview', icon: BarChart3 },
  { id: 'users', name: 'Users', icon: Users },
  { id: 'errors', name: 'Errors', icon: AlertTriangle },
  { id: 'integrations', name: 'Integrations', icon: Link2 },
  { id: 'system', name: 'System', icon: Server },
];

const ObservabilityDashboard = () => {
  const navigate = useNavigate();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedFirmId, setSelectedFirmId] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check super admin access on mount
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const result = await firmService.checkSuperAdmin();
        if (!result.is_super_admin) {
          navigate('/');
          return;
        }
        setIsSuperAdmin(true);
      } catch (err) {
        console.error('Error checking super admin access:', err);
        navigate('/');
      } finally {
        setCheckingAccess(false);
      }
    };
    checkAccess();
  }, [navigate]);

  // Fetch overview data when super admin confirmed or firm changes
  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await observabilityService.getOverview(selectedFirmId);
      setOverviewData(data);
    } catch (err) {
      console.error('Error fetching overview data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFirmId]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOverview();
    }
  }, [isSuperAdmin, fetchOverview]);

  const handleFirmChange = (firmId) => {
    setSelectedFirmId(firmId);
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return null;
  }

  const renderTabContent = () => {
    if (activeTab === 'overview') {
      return <OverviewTab data={overviewData} loading={loading} />;
    }
    if (activeTab === 'users') {
      return <UsersTab selectedFirmId={selectedFirmId} />;
    }
    if (activeTab === 'errors') {
      return <ErrorsTab selectedFirmId={selectedFirmId} />;
    }
    if (activeTab === 'integrations') {
      return <IntegrationsTab selectedFirmId={selectedFirmId} />;
    }
    if (activeTab === 'system') {
      return <SystemTab />;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center">
              <BarChart3 className="h-6 w-6 text-gray-500 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Observability Dashboard</h1>
            </div>
            <p className="mt-2 text-gray-600">
              Platform health and usage overview
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation - Desktop */}
          <div className="hidden lg:block lg:w-64">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-gray-100 text-yellow-600 border-l-4 border-yellow-500'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon className="h-4 w-4 mr-3" />
                      <span>{tab.name}</span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Mobile Tab Bar */}
          <div className="lg:hidden overflow-x-auto">
            <div className="flex space-x-1 min-w-max">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1">
            {/* Firm Picker */}
            <div className="mb-6">
              <ObservabilityFirmPicker
                selectedFirmId={selectedFirmId}
                onFirmChange={handleFirmChange}
              />
            </div>

            {/* Tab Content */}
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObservabilityDashboard;
